// MHM-5 — Hub Settings page (display name / notification prefs /
// COD threshold / SLA windows). Validation client-side + server-side.
import { useEffect, useState, useMemo } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import RoleGate from "../../components/RoleGate";
import MHMSubNav from "../../components/hub/MHMSubNav";
import api from "../../services/api";

const PREFS_LABELS = {
  sla_email: "SLA email alerts",
  sla_inapp: "SLA in-app banners",
  daily_digest: "Daily digest email (08:00 IST)",
  escalation_email: "Escalation email to admin on red alerts",
};

const SLA_LABELS = {
  pickup_yellow_min: { label: "Pickup yellow alert (minutes)", min: 5, max: 240 },
  pickup_red_min:    { label: "Pickup red alert (minutes)",    min: 5, max: 240 },
  stuck_red_hours:   { label: "Stuck-order red alert (hours)", min: 1, max: 72 },
  hub_overload_yellow_pct: { label: "Hub overload yellow (%)", min: 10, max: 100 },
  hub_overload_red_pct:    { label: "Hub overload red (%)",    min: 10, max: 100 },
};


function Card({ title, children, testid }) {
  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-5"
             data-testid={testid}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, children, hint, error }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
      {hint && !error && <span className="block text-xs text-slate-400 dark:text-slate-500 mt-1">{hint}</span>}
      {error && <span className="block text-xs text-rose-600 mt-1">{error}</span>}
    </label>
  );
}


function Inner() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/settings");
      setD(data.settings); setForm(data.settings);
    } catch (e) {
      toast.error(e.userMessage || "Failed to load settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (!d || !form) return false;
    return JSON.stringify(d) !== JSON.stringify(form);
  }, [d, form]);

  const validate = (f) => {
    const e = {};
    const w = f.sla_windows || {};
    if (w.pickup_yellow_min != null && w.pickup_red_min != null
        && Number(w.pickup_yellow_min) >= Number(w.pickup_red_min)) {
      e.pickup_yellow_min = "Yellow must be < Red";
    }
    if (w.hub_overload_yellow_pct != null && w.hub_overload_red_pct != null
        && Number(w.hub_overload_yellow_pct) >= Number(w.hub_overload_red_pct)) {
      e.hub_overload_yellow_pct = "Yellow % must be < Red %";
    }
    if (f.cod_threshold_rupees != null && Number(f.cod_threshold_rupees) < 0) {
      e.cod_threshold_rupees = "Must be ≥ 0";
    }
    for (const [k, meta] of Object.entries(SLA_LABELS)) {
      const v = w[k]; if (v == null) continue;
      const n = Number(v);
      if (Number.isNaN(n) || n < meta.min || n > meta.max) {
        e[k] = `Range ${meta.min}–${meta.max}`;
      }
    }
    return e;
  };

  useEffect(() => { if (form) setErrors(validate(form)); }, [form]);

  const save = async () => {
    const e = validate(form);
    if (Object.keys(e).length) {
      setErrors(e); toast.error("Fix validation errors first"); return;
    }
    setSaving(true);
    try {
      const body = {
        display_name: form.display_name ?? null,
        cod_threshold_rupees: Number(form.cod_threshold_rupees),
        notification_prefs: form.notification_prefs,
        sla_windows: Object.fromEntries(Object.entries(form.sla_windows).map(([k, v]) => [k, Number(v)])),
      };
      const { data } = await api.patch("/hub/settings", body);
      setD(data.settings); setForm(data.settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.userMessage || "Save failed");
    } finally { setSaving(false); }
  };

  const resetSla = async () => {
    try {
      const { data } = await api.post("/hub/settings/reset-sla-defaults");
      setD(data.settings); setForm(data.settings);
      toast.success("SLA windows reset to defaults");
    } catch (e) {
      toast.error(e.userMessage || "Reset failed");
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6" data-testid="mhm-settings-loading">
        <div className="animate-pulse space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="h-40 bg-slate-100 dark:bg-slate-800 rounded-2xl" />)}
        </div>
      </div>
    );
  }
  if (!form) return null;

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 space-y-4" data-testid="mhm-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']"
              data-testid="mhm-settings-title">Hub Settings</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Configure per-hub thresholds, COD limits, and notification preferences.
          </p>
        </div>
        <button onClick={save}
                disabled={!dirty || Object.keys(errors).length > 0 || saving}
                data-testid="mhm-settings-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold
                           bg-[#FF6B00] text-white hover:bg-[#E55F00] disabled:opacity-40 disabled:cursor-not-allowed">
          <Save className="h-4 w-4"/> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <Card title="1 · Hub identity" testid="mhm-settings-identity">
        <Field label="Hub display name" hint="Shown in PDFs, emails, and the top bar.">
          <input type="text" maxLength={80} data-testid="mhm-settings-display-name"
                 className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                            bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                            focus:outline-none focus:ring-2 focus:ring-orange-300"
                 value={form.display_name || ""}
                 onChange={(e) => setForm({ ...form, display_name: e.target.value })}/>
        </Field>
      </Card>

      <Card title="2 · Notification preferences" testid="mhm-settings-prefs">
        <div className="space-y-2">
          {Object.entries(PREFS_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-3 py-1.5">
              <span className="text-sm text-slate-700 dark:text-slate-200">{label}</span>
              <input type="checkbox" data-testid={`mhm-settings-pref-${k}`}
                     checked={!!form.notification_prefs?.[k]}
                     onChange={(e) => setForm({
                       ...form,
                       notification_prefs: { ...(form.notification_prefs || {}), [k]: e.target.checked }
                     })}
                     className="h-4 w-4 accent-green-600"/>
            </label>
          ))}
        </div>
      </Card>

      <Card title="3 · COD threshold" testid="mhm-settings-cod">
        <Field label="Maximum COD amount (₹)"
               hint="Orders above this amount require special approval."
               error={errors.cod_threshold_rupees}>
          <input type="number" min="0" max="100000" step="100"
                 data-testid="mhm-settings-cod-threshold"
                 className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                            bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                            focus:outline-none focus:ring-2 focus:ring-orange-300"
                 value={form.cod_threshold_rupees ?? 0}
                 onChange={(e) => setForm({ ...form, cod_threshold_rupees: e.target.value })}/>
        </Field>
      </Card>

      <Card title="4 · SLA windows" testid="mhm-settings-sla">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(SLA_LABELS).map(([k, meta]) => (
            <Field key={k} label={meta.label}
                   hint={`Allowed range: ${meta.min}–${meta.max}`}
                   error={errors[k]}>
              <input type="number" min={meta.min} max={meta.max} step="1"
                     data-testid={`mhm-settings-sla-${k.replace(/_/g, "-")}`}
                     className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                                bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                                focus:outline-none focus:ring-2 focus:ring-orange-300"
                     value={form.sla_windows?.[k] ?? ""}
                     onChange={(e) => setForm({
                       ...form,
                       sla_windows: { ...(form.sla_windows || {}), [k]: e.target.value }
                     })}/>
            </Field>
          ))}
        </div>
        <button onClick={resetSla} type="button"
                data-testid="mhm-settings-sla-reset"
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700
                           dark:text-slate-400 dark:hover:text-slate-200 mt-2">
          <RotateCcw className="h-3.5 w-3.5"/> Reset to defaults
        </button>
      </Card>
    </div>
  );
}


export default function HubSettings() {
  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <Inner />
    </RoleGate>
  );
}
